import { AppLayout } from '@/components/layout/AppLayout'
import { AtlasPage } from '@/components/ui/AtlasPage'
import { PageTitle } from '@/components/ui/PageTitle'
import { Separator } from '@/components/ui/Separator'
import { PageNumber } from '@/components/ui/PageNumber'
import { Button } from '@/components/ui/Button'
import { useForm, usePage } from '@inertiajs/react'
import { useEffect, useState, useCallback } from 'react'
import type { SharedProps } from '@/types/inertia'
import { IconSelect } from '@/components/ui/IconSelect'
import { SUPPLY_CATEGORY_ICONS } from '@/lib/icons'

interface Photo {
  id: number
  url: string
  thumbnail_url: string
}

interface SupplyData {
  id: number
  name: string
  description: string | null
  category: string | null
  photo: Photo | null
}

interface Props {
  supply: SupplyData
}

const categoryOptions = [
  { value: '', label: '— Wybierz —' },
  { value: 'substrate', label: 'Podłoże' },
  { value: 'fertilizer', label: 'Nawóz' },
  { value: 'amendment', label: 'Dodatek' },
  { value: 'tool', label: 'Przyrząd' },
  { value: 'other', label: 'Inne' },
]

function SuppliesEdit({ supply }: Props) {
  const { errors } = usePage<SharedProps>().props
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)

  const { data, setData, put, processing } = useForm({
    supply: {
      name: supply.name || '',
      description: supply.description || '',
      category: supply.category || '',
      photo: null as File | null,
    },
  })

  const setField = useCallback((field: string, value: unknown) => {
    setData('supply', { ...data.supply, [field]: value })
  }, [data.supply, setData])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    put(`/supplies/${supply.id}`, { forceFormData: true })
  }

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setData('supply', { ...data.supply, photo: file })
    if (file) {
      setPhotoPreview(URL.createObjectURL(file))
    } else {
      setPhotoPreview(null)
    }
  }

  useEffect(() => {
    return () => { if (photoPreview) URL.revokeObjectURL(photoPreview) }
  }, [photoPreview])

  return (
    <AtlasPage>
      <PageTitle title="Edycja zapasu" subtitle={supply.name} />
      <Separator />

      {supply.photo && !photoPreview && (
        <div className="mb-md">
          <p className="font-label text-xs text-frame uppercase tracking-wide mb-sm">
            Obecne zdjęcie
          </p>
          <img
            src={supply.photo.thumbnail_url}
            alt={supply.name}
            className="h-24 w-24 object-cover border border-frame/30"
          />
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-md">
        <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
          <FormField label="Nazwa" error={errors?.name}>
            <input
              type="text"
              value={data.supply.name}
              onChange={(e) => setField('name', e.target.value)}
              className="form-input"
              required
            />
          </FormField>

          <FormField label="Kategoria" error={errors?.category}>
            <IconSelect value={data.supply.category} onChange={(v) => setField('category', v)} options={categoryOptions} iconMap={SUPPLY_CATEGORY_ICONS} />
          </FormField>
        </div>

        <FormField label="Opis" error={errors?.description}>
          <textarea
            value={data.supply.description}
            onChange={(e) => setField('description', e.target.value)}
            className="form-input min-h-[6rem]"
            placeholder="Opis zapasu — Andrzej wykorzysta go do rekomendacji..."
          />
        </FormField>

        <FormField label="Zmień zdjęcie">
          <input
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="block w-full font-label text-sm text-frame file:mr-sm file:border file:border-frame file:bg-paper-dark file:px-sm file:py-xs file:text-xs file:text-ink file:font-label"
          />
          {photoPreview && (
            <img src={photoPreview} alt="" className="mt-sm h-24 w-24 object-cover border border-frame/30" />
          )}
        </FormField>

        <Separator />

        <div className="flex items-center justify-center gap-md">
          <Button type="submit" disabled={processing}>
            Zapisz zmiany
          </Button>
          <Button href={`/supplies/${supply.id}`} variant="secondary">
            Anuluj
          </Button>
        </div>
      </form>

      <PageNumber number={13} />
    </AtlasPage>
  )
}

function FormField({
  label,
  error,
  children,
}: {
  label: string
  error?: string[]
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="block font-label text-xs text-frame uppercase tracking-wide mb-xs">
        {label}
      </label>
      {children}
      {error && (
        <p className="text-xs text-danger mt-xs">{Array.isArray(error) ? error.join(', ') : error}</p>
      )}
    </div>
  )
}

SuppliesEdit.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
export default SuppliesEdit
